/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Notes — split-view surface (Figma Ping-Desktop 3746:7689).
//
// Left  sidebar : "+ New note" button, search, All / Favorites tabs,
//                 note rows (star + title + date + 3-dot menu).
// Right editor  : title input, metadata row (created / modified /
//                 word-count), rich-text body area, formatting toolbar,
//                 sync-status indicator.
//
// PRESENTATIONAL + platform-agnostic (RN + NativeWind). It is wired to
// the live `useNotes()` list by the host; the host owns the LOCAL-FIRST
// persist (`webNotesPersister.put` / `.delete`) AND the best-effort
// DeviceGroup `com.ping.note_upsert` / `com.ping.note_delete` send.
// This component never touches a backing store — it renders `notes` and
// calls `onUpsert` / `onDelete` on edits. className-first; inline style
// is not used here (all colors map to existing Tailwind tokens shared
// with the legacy Notes/Tasks surfaces).

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	FlatList,
	type ListRenderItem,
	Pressable,
	Text,
	TextInput,
	View,
} from "react-native";

import type { NoteRecord } from "../sync/persistenceAdapters";
import { buildPreview, notesStrings } from "./legacy";

/** Sidebar tab filter. Favorites are notes tagged with `FAVORITE_TAG`. */
type NotesTab = "all" | "favorites";

/** Tag used to mark a note as a favorite (kept on `NoteRecord.tags`). */
const FAVORITE_TAG = "favorite";

/** Host-supplied sync indicator for the active note. */
export type NotesSyncState = "synced" | "syncing" | "local-only";

export interface NotesScreenProps {
	/** Live, most-recently-edited-first list from `useNotes(persister)`. */
	readonly notes: readonly NoteRecord[];
	/**
	 * LOCAL-FIRST upsert. The host persists to the local store and
	 * best-effort sends `com.ping.note_upsert` to the DeviceGroup. Called
	 * on create, title/body edit (debounced by the screen), and favorite
	 * toggle.
	 */
	readonly onUpsert: (note: NoteRecord) => void;
	/** Delete: host removes locally + sends `com.ping.note_delete`. */
	readonly onDelete: (id: string) => void;
	/**
	 * Mints a fresh stable note id (hex `note_id` on the wire). Host owns
	 * id generation so the screen stays free of `crypto`/platform deps.
	 */
	readonly makeNoteId: () => string;
	/** Per-note sync state for the editor's status indicator. */
	readonly syncState?: NotesSyncState | undefined;
}

const ROW_HEIGHT_ESTIMATE = 72;

export const NotesScreen: React.FC<NotesScreenProps> = ({
	notes,
	onUpsert,
	onDelete,
	makeNoteId,
	syncState,
}) => {
	const [query, setQuery] = useState("");
	const [tab, setTab] = useState<NotesTab>("all");
	const [selectedId, setSelectedId] = useState<string | null>(null);

	// Keep a selection even as the live list re-sorts after each edit.
	// Auto-select the first note once data arrives if nothing is picked.
	useEffect(() => {
		if (selectedId !== null) {
			if (!notes.some((n) => n.id === selectedId)) {
				setSelectedId(notes.length > 0 ? (notes[0]?.id ?? null) : null);
			}
			return;
		}
		if (notes.length > 0) setSelectedId(notes[0]?.id ?? null);
	}, [notes, selectedId]);

	const filtered = useMemo<readonly NoteRecord[]>(() => {
		const q = query.trim().toLowerCase();
		return notes.filter((n) => {
			if (tab === "favorites" && !(n.tags ?? []).includes(FAVORITE_TAG)) {
				return false;
			}
			if (q.length === 0) return true;
			return n.title.toLowerCase().includes(q);
		});
	}, [notes, query, tab]);

	const selected = useMemo(
		() => notes.find((n) => n.id === selectedId) ?? null,
		[notes, selectedId],
	);

	const handleNewNote = useCallback(() => {
		const id = makeNoteId();
		onUpsert({ id, title: "", body: "", updatedAt: Date.now() });
		setSelectedId(id);
	}, [makeNoteId, onUpsert]);

	const handleToggleFavorite = useCallback(
		(note: NoteRecord) => {
			const has = (note.tags ?? []).includes(FAVORITE_TAG);
			const tags = has
				? (note.tags ?? []).filter((t) => t !== FAVORITE_TAG)
				: [...(note.tags ?? []), FAVORITE_TAG];
			onUpsert({
				id: note.id,
				title: note.title,
				body: note.body,
				updatedAt: Date.now(),
				...(tags.length > 0 ? { tags } : {}),
			});
		},
		[onUpsert],
	);

	const handleDelete = useCallback(
		(id: string) => {
			onDelete(id);
			if (selectedId === id) setSelectedId(null);
		},
		[onDelete, selectedId],
	);

	const keyExtractor = useCallback((n: NoteRecord) => n.id, []);

	const renderItem: ListRenderItem<NoteRecord> = useCallback(
		({ item }) => (
			<NoteRow
				note={item}
				active={item.id === selectedId}
				onPress={() => setSelectedId(item.id)}
				onToggleFavorite={() => handleToggleFavorite(item)}
				onDelete={() => handleDelete(item.id)}
			/>
		),
		[selectedId, handleToggleFavorite, handleDelete],
	);

	const getItemLayout = useCallback(
		(_: ArrayLike<NoteRecord> | null | undefined, index: number) => ({
			length: ROW_HEIGHT_ESTIMATE,
			offset: ROW_HEIGHT_ESTIMATE * index,
			index,
		}),
		[],
	);

	return (
		<View
			accessibilityLabel={notesStrings.screenTitle}
			className="flex-1 flex flex-row bg-light-surface-primary dark:bg-[#11131A]"
		>
			{/* ── Left sidebar ─────────────────────────────────────── */}
			<View className="w-[300px] flex flex-col border-r border-light-border-secondary dark:border-white/[0.06]">
				<View className="flex flex-row items-center justify-between px-lg py-md">
					<Text className="font-sans text-headline font-semibold text-light-text-primary dark:text-white">
						{notesStrings.screenTitle}
					</Text>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel={notesStrings.newNoteCta}
						onPress={handleNewNote}
						className="flex flex-row items-center px-md py-xs rounded-md bg-light-accent-indigo active:opacity-80"
					>
						<Text className="font-sans text-footnote font-semibold text-white">
							{`+ ${notesStrings.newNoteCta}`}
						</Text>
					</Pressable>
				</View>

				<NotesSearchBar value={query} onChange={setQuery} />

				<View className="flex flex-row px-lg pb-sm">
					<TabPill
						label="All"
						active={tab === "all"}
						onPress={() => setTab("all")}
					/>
					<TabPill
						label="Favorites"
						active={tab === "favorites"}
						onPress={() => setTab("favorites")}
					/>
				</View>

				{filtered.length === 0 ? (
					<SidebarEmpty query={query} tab={tab} />
				) : (
					<FlatList<NoteRecord>
						data={filtered}
						renderItem={renderItem}
						keyExtractor={keyExtractor}
						getItemLayout={getItemLayout}
						windowSize={10}
						initialNumToRender={20}
						maxToRenderPerBatch={15}
						removeClippedSubviews={false}
					/>
				)}
			</View>

			{/* ── Right editor ─────────────────────────────────────── */}
			{selected !== null ? (
				<NoteEditorPane
					key={selected.id}
					note={selected}
					syncState={syncState}
					onUpsert={onUpsert}
				/>
			) : (
				<EditorEmptyState onNewNote={handleNewNote} />
			)}
		</View>
	);
};

// ─────────────────────────────────────────────────────────────────────
// Editor pane

interface NoteEditorPaneProps {
	readonly note: NoteRecord;
	readonly syncState?: NotesSyncState | undefined;
	readonly onUpsert: (note: NoteRecord) => void;
}

const SAVE_DEBOUNCE_MS = 600;

function NoteEditorPane({
	note,
	syncState,
	onUpsert,
}: NoteEditorPaneProps): JSX.Element {
	// Local draft so typing stays responsive; the live list re-sorts only
	// after the debounced save flushes the upsert.
	const [title, setTitle] = useState(note.title);
	const [body, setBody] = useState(note.body);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// `key={note.id}` on the parent remounts this pane per note, so the
	// initial state above already reflects the selected note. No sync
	// effect needed — keeps the draft from being clobbered mid-edit.

	const flush = useCallback(
		(nextTitle: string, nextBody: string) => {
			onUpsert({
				id: note.id,
				title: nextTitle,
				body: nextBody,
				updatedAt: Date.now(),
				...(note.tags !== undefined ? { tags: note.tags } : {}),
			});
		},
		[note.id, note.tags, onUpsert],
	);

	const scheduleSave = useCallback(
		(nextTitle: string, nextBody: string) => {
			if (timer.current !== null) clearTimeout(timer.current);
			timer.current = setTimeout(() => {
				flush(nextTitle, nextBody);
			}, SAVE_DEBOUNCE_MS);
		},
		[flush],
	);

	useEffect(
		() => () => {
			if (timer.current !== null) clearTimeout(timer.current);
		},
		[],
	);

	const onChangeTitle = useCallback(
		(v: string) => {
			setTitle(v);
			scheduleSave(v, body);
		},
		[body, scheduleSave],
	);

	const onChangeBody = useCallback(
		(v: string) => {
			setBody(v);
			scheduleSave(title, v);
		},
		[title, scheduleSave],
	);

	const wordCount = useMemo(() => {
		const trimmed = body.trim();
		return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
	}, [body]);

	return (
		<View className="flex-1 flex flex-col bg-light-surface-primary dark:bg-[#11131A]">
			<TextInput
				value={title}
				onChangeText={onChangeTitle}
				placeholder={notesStrings.noteTitlePlaceholder}
				accessibilityLabel={notesStrings.noteTitleLabel}
				className="
					px-xl pt-lg pb-sm font-sans text-title2 font-semibold
					text-light-text-primary dark:text-white
					placeholder:text-light-text-tertiary dark:placeholder:text-white/40
				"
			/>

			{/* Metadata row */}
			<View className="flex flex-row items-center px-xl pb-md">
				<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/55">
					{`Modified ${formatDate(note.updatedAt)}`}
				</Text>
				<Text className="mx-sm font-sans text-caption text-light-text-tertiary dark:text-white/40">
					·
				</Text>
				<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/55">
					{`${wordCount} ${wordCount === 1 ? "word" : "words"}`}
				</Text>
				<View className="flex-1" />
				<SyncStatus state={syncState ?? "synced"} />
			</View>

			<View className="h-px mx-xl bg-light-border-secondary dark:bg-white/[0.06]" />

			{/* Body area */}
			<TextInput
				value={body}
				onChangeText={onChangeBody}
				placeholder={notesStrings.noteBodyPlaceholder}
				accessibilityLabel={notesStrings.noteBodyLabel}
				multiline
				textAlignVertical="top"
				className="
					flex-1 px-xl py-md font-sans text-body leading-[22px]
					text-light-text-primary dark:text-white/90
					placeholder:text-light-text-tertiary dark:placeholder:text-white/40
				"
			/>

			<FormattingToolbar />
		</View>
	);
}

// ─────────────────────────────────────────────────────────────────────
// Pieces

interface NotesSearchBarProps {
	readonly value: string;
	readonly onChange: (next: string) => void;
}

function NotesSearchBar({ value, onChange }: NotesSearchBarProps): JSX.Element {
	return (
		<View
			className="
				flex flex-row items-center
				mx-lg mb-sm px-md py-sm
				rounded-md border
				bg-light-surface-secondary border-light-border-secondary
				dark:bg-white/[0.05] dark:border-white/[0.06]
			"
		>
			<TextInput
				value={value}
				onChangeText={onChange}
				placeholder={notesStrings.searchPlaceholder}
				accessibilityLabel={notesStrings.searchPlaceholder}
				autoCorrect={false}
				autoCapitalize="none"
				className="
					flex-1 p-0 font-sans text-footnote
					text-light-text-primary dark:text-white
					placeholder:text-light-text-tertiary dark:placeholder:text-white/40
				"
			/>
			{value.length > 0 ? (
				<Pressable
					accessibilityRole="button"
					accessibilityLabel={notesStrings.searchClearLabel}
					hitSlop={8}
					onPress={() => onChange("")}
				>
					<Text className="px-xs font-sans text-headline font-semibold text-light-text-secondary dark:text-white/55">
						×
					</Text>
				</Pressable>
			) : null}
		</View>
	);
}

interface TabPillProps {
	readonly label: string;
	readonly active: boolean;
	readonly onPress: () => void;
}

function TabPill({ label, active, onPress }: TabPillProps): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={label}
			accessibilityState={{ selected: active }}
			onPress={onPress}
			className={`
				px-md py-xs mr-sm rounded-full active:opacity-70
				${active ? "bg-light-accent-indigo/15" : "bg-transparent"}
			`}
		>
			<Text
				className={`
					font-sans text-footnote font-semibold
					${active ? "text-light-accent-indigo" : "text-light-text-secondary dark:text-white/55"}
				`}
			>
				{label}
			</Text>
		</Pressable>
	);
}

interface NoteRowProps {
	readonly note: NoteRecord;
	readonly active: boolean;
	readonly onPress: () => void;
	readonly onToggleFavorite: () => void;
	readonly onDelete: () => void;
}

function NoteRow({
	note,
	active,
	onPress,
	onToggleFavorite,
	onDelete,
}: NoteRowProps): JSX.Element {
	const [menuOpen, setMenuOpen] = useState(false);
	const isFavorite = (note.tags ?? []).includes(FAVORITE_TAG);

	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={note.title || notesStrings.noteTitlePlaceholder}
			accessibilityState={{ selected: active }}
			onPress={onPress}
			className={`
				flex flex-row items-start
				px-lg py-md
				border-b border-light-border-secondary dark:border-white/[0.04]
				active:bg-light-surface-tertiary dark:active:bg-white/[0.06]
				${active ? "bg-light-accent-indigo/10" : "bg-transparent"}
			`}
		>
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={isFavorite ? "Unfavorite" : "Favorite"}
				accessibilityState={{ selected: isFavorite }}
				hitSlop={8}
				onPress={onToggleFavorite}
				className="mr-sm pt-[2px] active:opacity-60"
			>
				<Text
					className={`font-sans text-footnote ${
						isFavorite
							? "text-light-accent-amber"
							: "text-light-text-tertiary dark:text-white/40"
					}`}
				>
					{isFavorite ? "★" : "☆"}
				</Text>
			</Pressable>

			<View className="flex-1">
				<Text
					numberOfLines={1}
					className="font-sans text-body font-semibold text-light-text-primary dark:text-white mb-[2px]"
				>
					{note.title || notesStrings.noteTitlePlaceholder}
				</Text>
				<View className="flex flex-row items-center">
					<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/40 mr-sm">
						{formatDate(note.updatedAt)}
					</Text>
					<Text
						numberOfLines={1}
						className="flex-1 font-sans text-caption text-light-text-secondary dark:text-white/55"
					>
						{buildPreview(note.body)}
					</Text>
				</View>
			</View>

			<View className="ml-sm">
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="Note options"
					accessibilityState={{ expanded: menuOpen }}
					hitSlop={8}
					onPress={() => setMenuOpen((v) => !v)}
					className="px-xs active:opacity-60"
				>
					<Text className="font-sans text-headline font-semibold text-light-text-secondary dark:text-white/55">
						⋯
					</Text>
				</Pressable>
				{menuOpen ? (
					<View className="absolute right-0 top-[24px] z-10 min-w-[140px] rounded-md border border-light-border-secondary bg-light-surface-primary dark:border-white/[0.08] dark:bg-[#1A1D26] py-xs">
						<Pressable
							accessibilityRole="button"
							accessibilityLabel={notesStrings.optionMenu.delete}
							onPress={() => {
								setMenuOpen(false);
								onDelete();
							}}
							className="px-md py-sm active:bg-light-surface-tertiary dark:active:bg-white/[0.06]"
						>
							<Text className="font-sans text-footnote font-semibold text-light-accent-rose">
								{notesStrings.optionMenu.delete}
							</Text>
						</Pressable>
					</View>
				) : null}
			</View>
		</Pressable>
	);
}

interface SyncStatusProps {
	readonly state: NotesSyncState;
}

function SyncStatus({ state }: SyncStatusProps): JSX.Element {
	const { label, colorClass } = ((): {
		label: string;
		colorClass: string;
	} => {
		switch (state) {
			case "syncing":
				return {
					label: notesStrings.editor.saving,
					colorClass: "text-light-accent-amber",
				};
			case "local-only":
				return {
					label: "Saved on this device",
					colorClass: "text-light-text-tertiary dark:text-white/55",
				};
			case "synced":
				return {
					label: notesStrings.editor.saved,
					colorClass: "text-light-accent-emerald",
				};
		}
	})();

	return (
		<Text
			accessibilityLabel={label}
			className={`font-sans text-caption font-medium ${colorClass}`}
		>
			{label}
		</Text>
	);
}

// Presentational formatting toolbar. The body is a plain multiline
// `TextInput` in this slice (rich-text storage is a follow-up); the
// toolbar mirrors the Figma chrome so the surface matches 1:1. Buttons
// are inert until the rich-text body model lands.
const TOOLBAR_GLYPHS: ReadonlyArray<{ glyph: string; label: string }> = [
	{ glyph: "B", label: "Bold" },
	{ glyph: "I", label: "Italic" },
	{ glyph: "U", label: "Underline" },
	{ glyph: "•", label: "Bulleted list" },
	{ glyph: "1.", label: "Numbered list" },
	{ glyph: "“", label: "Quote" },
];

function FormattingToolbar(): JSX.Element {
	return (
		<View className="flex flex-row items-center px-xl py-sm border-t border-light-border-secondary dark:border-white/[0.06]">
			{TOOLBAR_GLYPHS.map((g) => (
				<View
					key={g.label}
					accessibilityLabel={g.label}
					className="w-7 h-7 mr-xs items-center justify-center rounded-md"
				>
					<Text className="font-sans text-footnote font-semibold text-light-text-tertiary dark:text-white/40">
						{g.glyph}
					</Text>
				</View>
			))}
		</View>
	);
}

interface SidebarEmptyProps {
	readonly query: string;
	readonly tab: NotesTab;
}

function SidebarEmpty({ query, tab }: SidebarEmptyProps): JSX.Element {
	const title =
		tab === "favorites" && query.length === 0
			? "No favorites yet"
			: notesStrings.emptyTitle(query);
	return (
		<View className="flex-1 items-center justify-center px-lg">
			<Text className="font-sans text-footnote font-semibold text-light-text-primary dark:text-white text-center">
				{title}
			</Text>
		</View>
	);
}

interface EditorEmptyStateProps {
	readonly onNewNote: () => void;
}

function EditorEmptyState({ onNewNote }: EditorEmptyStateProps): JSX.Element {
	return (
		<View className="flex-1 items-center justify-center p-xl bg-light-surface-primary dark:bg-[#11131A]">
			<Text className="font-sans text-headline font-semibold text-light-text-primary dark:text-white mb-xs text-center">
				{notesStrings.emptyTitle("")}
			</Text>
			<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center max-w-[320px] leading-[20px] mb-md">
				{notesStrings.emptyBody}
			</Text>
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={notesStrings.newNoteCta}
				onPress={onNewNote}
				className="px-lg py-sm rounded-md bg-light-accent-indigo active:opacity-80"
			>
				<Text className="font-sans text-footnote font-semibold text-white">
					{`+ ${notesStrings.newNoteCta}`}
				</Text>
			</Pressable>
		</View>
	);
}

// Compact, locale-aware date label for note rows + metadata. Wall-clock
// ms in -> short human string. Never logs the value (note content is
// sensitive, but the timestamp is benign — kept tiny for the row).
function formatDate(ms: number): string {
	try {
		return new Date(ms).toLocaleDateString(undefined, {
			month: "short",
			day: "numeric",
		});
	} catch {
		return "";
	}
}
